import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCreateEntityRecord, useCreatePeriod } from '@/hooks/use-financial-data';
import { createFinancialYear, getEntityRecord } from '@/lib/api/financial';
import { WizardStepEntity } from './wizard-step-entity';
import { WizardStepClassification } from './wizard-step-classification';
import { WizardStepPeriod } from './wizard-step-period';
import { WizardStepMethod } from './wizard-step-method';
import { WizardStepSummary } from './wizard-step-summary';
import { PdfUploadWizard } from '../import/pdf-upload-wizard';
import { CsvImportFlow } from '../import/csv-import-flow';
import { ensureCsvImportTarget } from '../import/csv-import-flow-helpers';
import { XeroConnectFlow } from '../import/xero-connect-flow';
import { XeroMappingEditor, type MappingSuggestion, type StandardisedResult } from '../import/xero-mapping-editor';
import { XeroSyncPreview } from '../import/xero-sync-preview';
import { apiRequest } from '@/lib/queryClient';
import type {
  DataClassification,
  TrustGrade,
  PeriodType,
  CreateFinancialPeriodInput,
} from '@/types/financial';

export interface AddFinancialYearWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (periodId: string) => void;
  context: 'account' | 'client-group' | 'deal';
  accountId?: string; // Pre-set for account context
  clientGroupId?: string; // For client group context
  dealId?: string; // For deal context
  accounts?: Array<{ id: string; legalName: string; entityType: string }>; // For selection
}

export interface WizardState {
  // Step 1: Entity Selection (conditional)
  selectedAccountId?: string;

  // Step 2: Data Classification
  dataClassification?: DataClassification;
  trustGrade?: TrustGrade;

  // Step 3: Period Configuration
  financialYearEnd?: Date;
  periodType?: PeriodType;
  periodLabel?: string;
  startDate?: Date;
  endDate?: Date;
  monthsCovered?: number;
  annualisationFactor?: number;

  // Step 4: Data Entry Method
  entryMethod?: 'manual' | 'csv' | 'pdf' | 'xero';
}

export function resolveLatestCreatedPeriodId(
  refValue: string | null,
  stateValue: string | null
): string | null {
  return refValue ?? stateValue;
}

/**
 * Add Financial Year Wizard
 *
 * 5-step guided wizard for creating financial periods:
 * - Step 1: Entity Selection (conditional - skipped for Account context)
 * - Step 2: Data Classification
 * - Step 3: Period Configuration
 * - Step 4: Data Entry Method
 * - Step 5: Summary & Create
 *
 * Entry points:
 * - Account detail → Financials tab (skips Step 1)
 * - Client Group → Financials tab (shows Step 1)
 * - Deal → Financials tab (shows Step 1)
 */
export const AddFinancialYearWizard: React.FC<AddFinancialYearWizardProps> = ({
  open,
  onClose,
  onSuccess,
  context,
  accountId,
  clientGroupId: _clientGroupId,
  dealId: _dealId,
  accounts = [],
}) => {
  const { toast } = useToast();
  const createEntityMutation = useCreateEntityRecord();
  const createPeriodMutation = useCreatePeriod();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(context === 'account' ? 2 : 1); // Skip step 1 for account
  const [wizardState, setWizardState] = useState<WizardState>({
    selectedAccountId: context === 'account' ? accountId : undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PDF sub-flow: when entryMethod === 'pdf', we create the period first then show upload
  const [showPdfUpload, setShowPdfUpload] = useState(false);
  const [createdPeriodId, setCreatedPeriodId] = useState<string | null>(null);
  const createdPeriodIdRef = useRef<string | null>(null);

  // CSV sub-flow (EDG-531): when entryMethod === 'csv', inline CsvImportFlow
  const [showCsvImport, setShowCsvImport] = useState(false);

  // Xero sub-flow states
  const [showXeroConnect, setShowXeroConnect] = useState(false);
  const [showXeroSync, setShowXeroSync] = useState(false);
  const [showXeroMapping, setShowXeroMapping] = useState(false);
  const [showXeroPreview, setShowXeroPreview] = useState(false);

  // Xero sub-flow data
  const [xeroConnectionId, setXeroConnectionId] = useState<string | null>(null);
  const [xeroOrgName, setXeroOrgName] = useState<string | null>(null);
  const [xeroSyncResult, setXeroSyncResult] = useState<StandardisedResult | null>(null);
  const [xeroSuggestedMappings, setXeroSuggestedMappings] = useState<MappingSuggestion[]>([]);
  const [xeroTrustGrade, setXeroTrustGrade] = useState<'xero_finalised' | 'xero_draft'>('xero_draft');
  const [xeroCreatedPeriodId, setXeroCreatedPeriodId] = useState<string | null>(null);
  const [xeroEntityRecordId, setXeroEntityRecordId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Calculate total steps (4 for account, 5 for client-group/deal)
  const totalSteps = context === 'account' ? 4 : 5;

  const updateWizardState = (updates: Partial<WizardState>) => {
    setWizardState((prev) => ({ ...prev, ...updates }));
  };

  const rememberCreatedPeriodId = (periodId: string | null) => {
    createdPeriodIdRef.current = periodId;
    setCreatedPeriodId(periodId);
  };

  const createPeriodForDataEntry = async (): Promise<{ periodId: string; entityRecordId: string }> => {
    if (!wizardState.selectedAccountId) {
      throw new Error('No account selected');
    }

    const target = await ensureCsvImportTarget({
      accountId: wizardState.selectedAccountId,
      periodInput: {
        periodType: wizardState.periodType || 'historical',
        periodLabel: wizardState.periodLabel || '',
        periodStartDate: wizardState.startDate?.toISOString().split('T')[0] || '',
        periodEndDate: wizardState.endDate?.toISOString().split('T')[0] || '',
        monthsInPeriod: wizardState.monthsCovered || 12,
        periodSequence: 1,
        dataClassification: wizardState.dataClassification || 'accountant_prepared',
        trustGrade: wizardState.trustGrade || 'credit_grade',
        annualisationFactor: wizardState.annualisationFactor
          ? wizardState.annualisationFactor.toFixed(4)
          : null,
      },
      deps: {
        getEntityRecord,
        createEntityRecord: async (input) => createEntityMutation.mutateAsync(input),
        createPeriod: async (input) => createPeriodMutation.mutateAsync(input),
      },
    });

    return {
      periodId: target.periodId,
      entityRecordId: target.entityFinancialRecordId,
    };
  };

  /**
   * Create the entity record + period and return the period ID.
   * Used by the PDF sub-flow to get a periodId before uploading.
   */
  const createPeriodForPdfUpload = async (): Promise<string> => {
    const created = await createPeriodForDataEntry();
    return created.periodId;
  };

  const handleNext = () => {
    // Determine if we are on the Method step (effectiveStep === 4)
    const effectiveStep = context === 'account' ? currentStep + 1 : currentStep;
    if (effectiveStep === 4 && wizardState.entryMethod === 'pdf') {
      // PDF sub-flow: show PDF upload form; period created on first upload attempt
      setShowPdfUpload(true);
      return;
    }
    if (effectiveStep === 4 && wizardState.entryMethod === 'csv') {
      // CSV sub-flow (EDG-531): show inline CsvImportFlow
      setShowCsvImport(true);
      return;
    }
    if (effectiveStep === 4 && wizardState.entryMethod === 'xero') {
      // Xero sub-flow: show Xero connection flow
      setShowXeroConnect(true);
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    // Xero sub-flow back navigation (reverse order)
    if (showXeroPreview) {
      setShowXeroPreview(false);
      setShowXeroMapping(true);
      return;
    }
    if (showXeroMapping) {
      setShowXeroMapping(false);
      setShowXeroSync(false);
      setShowXeroConnect(true);
      return;
    }
    if (showXeroSync) {
      setShowXeroSync(false);
      setShowXeroConnect(true);
      return;
    }
    if (showXeroConnect) {
      setShowXeroConnect(false);
      return;
    }
    if (showCsvImport) {
      setShowCsvImport(false);
      return;
    }
    if (showPdfUpload) {
      setShowPdfUpload(false);
      setCreatedPeriodId(null);
      return;
    }
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Clear every step and sub-flow value, so the next run starts fresh.
  const resetWizard = () => {
    setWizardState({
      selectedAccountId: context === 'account' ? accountId : undefined,
    });
    setCurrentStep(context === 'account' ? 2 : 1);
    setShowPdfUpload(false);
    setShowCsvImport(false);
    setShowXeroConnect(false);
    setShowXeroSync(false);
    setShowXeroMapping(false);
    setShowXeroPreview(false);
    setXeroConnectionId(null);
    setXeroOrgName(null);
    setXeroSyncResult(null);
    setXeroSuggestedMappings([]);
    setXeroTrustGrade('xero_draft');
    setXeroCreatedPeriodId(null);
    setXeroEntityRecordId(null);
    setIsSyncing(false);
    setSyncError(null);
    rememberCreatedPeriodId(null);
  };

  // The parent keeps this instance mounted (issue 2227), and success paths
  // close the dialog without handleCancel. Reset whenever the dialog closes.
  useEffect(() => {
    if (!open) resetWizard();
  }, [open]);

  const handleCancel = () => {
    // A sub-flow may already have saved the year. Cancel does not delete it,
    // so tell the broker it exists (issue 2227).
    if (resolveLatestCreatedPeriodId(createdPeriodIdRef.current, createdPeriodId)) {
      toast({
        title: 'Financial year created',
        description: 'The year was saved with no data. You can add data to it, or delete it, in the Financials tab.',
      });
    }
    resetWizard();
    onClose();
  };

  const handleCreate = async () => {
    if (!wizardState.selectedAccountId) {
      toast({
        title: 'Error',
        description: 'No account selected',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const periodData: Omit<CreateFinancialPeriodInput, 'entityFinancialRecordId'> = {
        periodType: wizardState.periodType || 'historical',
        periodLabel: wizardState.periodLabel || '',
        periodStartDate: wizardState.startDate?.toISOString().split('T')[0] || '',
        periodEndDate: wizardState.endDate?.toISOString().split('T')[0] || '',
        monthsInPeriod: wizardState.monthsCovered || 12,
        periodSequence: 1,
        dataClassification: wizardState.dataClassification || 'accountant_prepared',
        trustGrade: wizardState.trustGrade || 'credit_grade',
        annualisationFactor: wizardState.annualisationFactor
          ? wizardState.annualisationFactor.toFixed(4)
          : null,
      };

      const { period: createdPeriod } = await createFinancialYear({
        entityRecord: {
          account_id: wizardState.selectedAccountId,
          financial_template: 'trading',
          last_financial_year_end: wizardState.financialYearEnd
            ? wizardState.financialYearEnd.toISOString().split('T')[0]
            : undefined,
        },
        period: periodData,
      });

      toast({
        title: 'Success',
        description: 'Financial year created successfully',
      });

      // Reset and close
      setWizardState({
        selectedAccountId: context === 'account' ? accountId : undefined,
      });
      setCurrentStep(context === 'account' ? 2 : 1);
      onSuccess?.(createdPeriod.id);
      onClose();
    } catch (error: unknown) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to create financial year';
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    // Xero sub-flow: Preview step
    if (showXeroPreview && xeroSyncResult && xeroConnectionId && xeroOrgName) {
      const periodStart = wizardState.startDate?.toISOString().split('T')[0] ?? '';
      const periodEnd = wizardState.endDate?.toISOString().split('T')[0] ?? '';
      return (
        <XeroSyncPreview
          financialPeriodId={xeroCreatedPeriodId ?? ''}
          entityRecordId={xeroEntityRecordId ?? ''}
          connectionId={xeroConnectionId}
          orgName={xeroOrgName}
          syncResult={xeroSyncResult}
          trustGrade={xeroTrustGrade}
          periodStartDate={periodStart}
          periodEndDate={periodEnd}
          onConfirm={() => {
            // Success: reset wizard and close
            const newPeriodId = xeroCreatedPeriodId;
            setWizardState({
              selectedAccountId: context === 'account' ? accountId : undefined,
            });
            setCurrentStep(context === 'account' ? 2 : 1);
            setShowXeroPreview(false);
            setShowXeroMapping(false);
            setShowXeroSync(false);
            setShowXeroConnect(false);
            setXeroConnectionId(null);
            setXeroOrgName(null);
            setXeroSyncResult(null);
            setXeroCreatedPeriodId(null);
            setXeroEntityRecordId(null);
            if (newPeriodId) onSuccess?.(newPeriodId);
            onClose();
          }}
          onBackToMapping={() => {
            setShowXeroPreview(false);
            setShowXeroMapping(true);
          }}
          onCancel={handleCancel}
        />
      );
    }

    // Xero sub-flow: Mapping editor step
    if (showXeroMapping && xeroConnectionId && xeroCreatedPeriodId && xeroEntityRecordId) {
      return (
        <XeroMappingEditor
          financialPeriodId={xeroCreatedPeriodId}
          entityRecordId={xeroEntityRecordId}
          connectionId={xeroConnectionId}
          suggestedMappings={xeroSuggestedMappings}
          onComplete={(result) => {
            setXeroSyncResult(result);
            setShowXeroMapping(false);
            setShowXeroPreview(true);
          }}
          onBack={() => {
            setShowXeroMapping(false);
            setShowXeroSync(false);
            setShowXeroConnect(true);
          }}
          onCancel={handleCancel}
        />
      );
    }

    // Xero sub-flow: Sync step — creates period, triggers sync, shows progress
    if (showXeroSync && xeroConnectionId && xeroOrgName) {
      const periodStart = wizardState.startDate?.toISOString().split('T')[0] ?? '';
      const periodEnd = wizardState.endDate?.toISOString().split('T')[0] ?? '';

      // If already synced (mapping already seeded), go directly to mapping
      const triggerSync = async () => {
        setIsSyncing(true);
        setSyncError(null);

        try {
          // Step 1: Create or get entity record + period
          let entityRecordId = xeroEntityRecordId;
          let periodId = xeroCreatedPeriodId;

          if (!entityRecordId || !periodId) {
            const resolvedAccountId = wizardState.selectedAccountId ?? accountId ?? '';

            // Get or create entity record (getEntityRecord already statically imported above)
            const existingEntityRecord = await getEntityRecord(resolvedAccountId);
            let resolvedEntityRecordId: string;
            if (existingEntityRecord) {
              resolvedEntityRecordId = existingEntityRecord.id;
            } else {
              const res = await apiRequest('POST', '/api/financial/entity-records', {
                accountId: resolvedAccountId,
                financialTemplate: 'trading',
                lastFinancialYearEnd: wizardState.financialYearEnd
                  ? wizardState.financialYearEnd.toISOString().split('T')[0]
                  : undefined,
              });
              const newRecord: { id: string } = await res.json();
              resolvedEntityRecordId = newRecord.id;
            }
            entityRecordId = resolvedEntityRecordId;
            setXeroEntityRecordId(entityRecordId);

            // Create period
            const periodRes = await apiRequest('POST', '/api/financial/periods', {
              entityFinancialRecordId: entityRecordId,
              periodType: wizardState.periodType ?? 'historical',
              periodLabel: wizardState.periodLabel ?? '',
              periodStartDate: periodStart,
              periodEndDate: periodEnd,
              monthsInPeriod: wizardState.monthsCovered ?? 12,
              periodSequence: 1,
              dataClassification: wizardState.dataClassification ?? 'accountant_prepared',
              trustGrade: wizardState.trustGrade ?? 'credit_grade',
              annualisationFactor: wizardState.annualisationFactor
                ? wizardState.annualisationFactor.toFixed(4)
                : null,
            });
            const period = await periodRes.json();
            periodId = period.id;
            setXeroCreatedPeriodId(periodId);
          }

          // Step 2: Trigger Xero sync
          const syncRes = await apiRequest('POST', '/api/xero/sync', {
            connectionId: xeroConnectionId,
            financialPeriodId: periodId,
            entityRecordId: entityRecordId,
            fromDate: periodStart,
            toDate: periodEnd,
          });

          if (!syncRes.ok) {
            const errText = await syncRes.text();
            throw new Error(errText || 'Xero sync failed');
          }

          const syncData = await syncRes.json();

          // Capture trust grade from sync response
          if (syncData.trustGrade) {
            setXeroTrustGrade(syncData.trustGrade as 'xero_finalised' | 'xero_draft');
          }
          if (syncData.suggestedMappings) {
            setXeroSuggestedMappings(syncData.suggestedMappings);
          }

          // Step 3: Advance to mapping editor
          setShowXeroSync(false);
          setShowXeroMapping(true);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Sync failed';
          setSyncError(message);
        } finally {
          setIsSyncing(false);
        }
      };

      return (
        <div className="space-y-6" data-testid="xero-sync-step">
          <div>
            <h3 className="text-lg font-semibold">Syncing from Xero</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Pulling financial data from {xeroOrgName}
            </p>
          </div>

          {isSyncing && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <div className="flex items-center gap-3">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">
                  Pulling data from {xeroOrgName}...
                </span>
              </div>
            </div>
          )}

          {syncError && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-4">
              <p className="text-sm text-destructive font-medium">Sync failed</p>
              <p className="text-xs text-muted-foreground mt-1">{syncError}</p>
              <button
                type="button"
                className="text-xs text-primary underline mt-2"
                onClick={() => { setSyncError(null); void triggerSync(); }}
              >
                Retry
              </button>
            </div>
          )}

          {!isSyncing && !syncError && (
            <p className="text-sm text-muted-foreground">
              Ready to sync. Click &quot;Start Sync&quot; to pull data from Xero.
            </p>
          )}

          <div className="flex justify-between pt-2">
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                onClick={() => { setShowXeroSync(false); setShowXeroConnect(true); }}
                disabled={isSyncing}
                data-testid="button-wizard-back"
              >
                Back
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                onClick={handleCancel}
                disabled={isSyncing}
                data-testid="button-wizard-cancel"
              >
                Cancel
              </button>
            </div>
            {!isSyncing && (
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                onClick={() => { void triggerSync(); }}
                data-testid="button-sync-next"
              >
                Start Sync
              </button>
            )}
          </div>
        </div>
      );
    }

    // Xero sub-flow: Connect step
    if (showXeroConnect) {
      const resolvedAccountId = wizardState.selectedAccountId ?? accountId ?? '';
      return (
        <XeroConnectFlow
          accountId={resolvedAccountId}
          onConnectionReady={(connectionId, orgName) => {
            setXeroConnectionId(connectionId);
            setXeroOrgName(orgName);
            setShowXeroConnect(false);
            setShowXeroSync(true);
          }}
          onBack={() => {
            setShowXeroConnect(false);
          }}
          onCancel={handleCancel}
        />
      );
    }

    // CSV sub-flow (EDG-531): inline CsvImportFlow with create-new-fy mode
    if (showCsvImport && wizardState.selectedAccountId) {
      return (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">CSV/Excel Import</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a CSV file and map columns to financial line items
            </p>
          </div>
          <CsvImportFlow
            accountId={wizardState.selectedAccountId}
            periodContext={{
              mode: 'create-new-fy-then-import',
              newFyDraft: { label: wizardState.periodLabel || 'New FY' },
              ensureTargetPeriod: async () => {
                const created = await createPeriodForDataEntry();
                rememberCreatedPeriodId(created.periodId);
                return {
                  periodId: created.periodId,
                  entityFinancialRecordId: created.entityRecordId,
                };
              },
              onPeriodCreated: (periodId: string) => {
                rememberCreatedPeriodId(periodId);
              },
            }}
            asDialog={false}
            onComplete={() => {
              const completedPeriodId = resolveLatestCreatedPeriodId(
                createdPeriodIdRef.current,
                createdPeriodId
              );
              if (completedPeriodId) onSuccess?.(completedPeriodId);
              onClose();
            }}
            onCancel={() => {
              setShowCsvImport(false);
              updateWizardState({ entryMethod: 'manual' });
            }}
          />
        </div>
      );
    }

    // PDF sub-flow: show upload wizard (period created on first upload)
    if (showPdfUpload) {
      // Provide a lazily-resolved periodId: create period on first need
      const getPeriodId = async (): Promise<string> => {
        const existingPeriodId = resolveLatestCreatedPeriodId(
          createdPeriodIdRef.current,
          createdPeriodId
        );
        if (existingPeriodId) return existingPeriodId;
        const newPeriodId = await createPeriodForPdfUpload();
        rememberCreatedPeriodId(newPeriodId);
        return newPeriodId;
      };

      return (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Upload PDF</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upload your accountant-prepared financials or tax return PDF
            </p>
          </div>
          <PdfUploadWizard
            getPeriodId={getPeriodId}
            onComplete={(jobId) => {
              // Extraction done — close wizard and notify success
              toast({
                title: 'PDF uploaded',
                description: 'Extraction complete. Review extracted data.',
              });
              const completedPeriodId = resolveLatestCreatedPeriodId(
                createdPeriodIdRef.current,
                createdPeriodId
              );
              if (completedPeriodId) onSuccess?.(completedPeriodId);
              onClose();
              // Store jobId in sessionStorage for F006 preview to pick up
              sessionStorage.setItem('extractionJobId', jobId);
            }}
            onManualEntry={() => {
              // Fall back: close PDF flow, reset to method step with manual selection
              setShowPdfUpload(false);
              rememberCreatedPeriodId(null);
              updateWizardState({ entryMethod: 'manual' });
            }}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        </div>
      );
    }

    // Adjust step numbers for account context (which skips step 1)
    const effectiveStep = context === 'account' ? currentStep + 1 : currentStep;

    switch (effectiveStep) {
      case 1:
        return (
          <WizardStepEntity
            accounts={accounts}
            selectedAccountId={wizardState.selectedAccountId}
            onSelect={(id) => updateWizardState({ selectedAccountId: id })}
            onNext={handleNext}
            onCancel={handleCancel}
          />
        );
      case 2:
        return (
          <WizardStepClassification
            dataClassification={wizardState.dataClassification}
            onSelect={(classification, grade) =>
              updateWizardState({ dataClassification: classification, trustGrade: grade })
            }
            onNext={handleNext}
            onBack={context === 'account' ? undefined : handleBack}
            onCancel={handleCancel}
          />
        );
      case 3:
        return (
          <WizardStepPeriod
            financialYearEnd={wizardState.financialYearEnd}
            periodType={wizardState.periodType}
            periodLabel={wizardState.periodLabel}
            startDate={wizardState.startDate}
            endDate={wizardState.endDate}
            monthsCovered={wizardState.monthsCovered}
            annualisationFactor={wizardState.annualisationFactor}
            onChange={updateWizardState}
            onNext={handleNext}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 4:
        return (
          <WizardStepMethod
            entryMethod={wizardState.entryMethod}
            onSelect={(method) => updateWizardState({ entryMethod: method })}
            onNext={handleNext}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 5:
        return (
          <WizardStepSummary
            wizardState={wizardState}
            accounts={accounts}
            onCreate={handleCreate}
            onBack={handleBack}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="max-w-2xl" data-testid="wizard-financial-year">
        <DialogHeader>
          <DialogTitle>Add Financial Year</DialogTitle>
          <div className="text-sm text-muted-foreground" data-testid="wizard-step-indicator">
            Step {currentStep} of {totalSteps}
          </div>
        </DialogHeader>

        <div className="mt-4">{renderStep()}</div>
      </DialogContent>
    </Dialog>
  );
};
